import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignName } from '../campaigns';

import { API_BASE } from '../apiBase';
const TOKEN_KEY = 'gardeno_admin_token';

const C = {
  bg: '#0a0a0b', panel: '#141416', panelAlt: '#1a1a1d', border: '#262629',
  text: '#f2f2f3', sub: '#8a8a90', accent: '#2D9E5F',
};

interface Detail {
  email: string; name: string; provider: string; is_guest: boolean; signed_up: boolean;
  country: string; city: string; created_at: string; plan: string; period: string;
  paywall: string; is_tester?: boolean;
  subscription?: {
    status: string; period: string;
    started_at: string | null; trial_ends_at: string | null;
    current_period_end: string | null; cancelled_at: string | null;
    first_payment_at: string | null; last_payment_at: string | null;
  };
  payments?: { event_type: string; amount_usd: number; currency: string; event_at: string }[];
  acquisition?: {
    attributed: boolean | null; campaign_id: number | null; ad_group_id: number | null;
    keyword_id: number | null; country: string | null; conversion_type: string | null; click_date: string | null;
  };
  onboarding: { completed: boolean; last_step: string; steps: { step_key: string; step_index: number; viewed_at: string }[]; answers: Record<string, string>; runs?: number };
  activity: { type: string; title: string; created_at: string; image_url?: string }[];
}

const EVENT_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  trial_start:  { bg: '#3a2a12', fg: '#F5A623', label: 'Trial started' },
  renewal:      { bg: '#123524', fg: '#4ADE80', label: 'Renewal' },
  purchase:     { bg: '#123524', fg: '#4ADE80', label: 'Purchase' },
  conversion:   { bg: '#123524', fg: '#4ADE80', label: 'Conversion' },
  refund:       { bg: '#3a1414', fg: '#F87171', label: 'Refund' },
  cancellation: { bg: '#2a1c1c', fg: '#c98a8a', label: 'Cancelled' },
};

function fmt(ts: string): string {
  try { return new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }); } catch { return ts; }
}
function flagEmoji(code: string): string {
  if (!code || code.length !== 2) return '';
  const cc = code.toUpperCase();
  return String.fromCodePoint(0x1f1e6 + cc.charCodeAt(0) - 65, 0x1f1e6 + cc.charCodeAt(1) - 65);
}

const PAYWALL_LABEL: Record<string, { text: string; color: string }> = {
  subscribed:        { text: 'Subscribed', color: '#4ADE80' },
  started_trial:     { text: 'Started free trial', color: '#F5A623' },
  comp:              { text: 'Complimentary', color: '#A78BFA' },
  reached_dashboard: { text: 'Crossed paywall + signup → reached app (no purchase)', color: '#60A5FA' },
  left_at_signup:    { text: 'Crossed paywall → closed app at signup', color: '#F87171' },
  left_at_paywall:   { text: 'Saw paywall → closed the app', color: '#F87171' },
  skipped:           { text: 'Reached paywall — did not purchase', color: '#F87171' },
  not_reached:       { text: 'Did not reach paywall', color: '#8a8a90' },
};
const ACT_ICON: Record<string, string> = { scan: '🌿', diagnose: '🩺', community_post: '💬', garden_add: '🪴' };
const ACT_LABEL: Record<string, string> = { scan: 'Plant scan', diagnose: 'Diagnosis', community_post: 'Community post', garden_add: 'Added to garden' };

function UserDetail() {
  const { email: rawEmail } = useParams<{ email: string }>();
  const navigate = useNavigate();
  const email = rawEmail ? decodeURIComponent(rawEmail) : '';
  const [d, setD] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  // Photo opened from the activity feed, shown full-size over the page.
  const [photo, setPhoto] = useState<{ url: string; caption: string } | null>(null);

  async function toggleTester() {
    if (!d) return;
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) { navigate('/admin'); return; }
    const next = !d.is_tester;
    setToggling(true);
    try {
      const res = await fetch(`${API_BASE}/admin/user/tester`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: d.email, is_tester: next }),
      });
      if (res.ok) setD({ ...d, is_tester: next });
    } catch { /* ignore */ }
    finally { setToggling(false); }
  }

  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) { navigate('/admin'); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/user?email=${encodeURIComponent(email)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.status === 403 || res.status === 401) { navigate('/admin'); return; }
        if (!res.ok) { setError(`Server error (${res.status})`); return; }
        const j = await res.json();
        setD(j.data ?? j);
      } catch { setError('Could not reach the server.'); }
      finally { setLoading(false); }
    })();
  }, [email, navigate]);

  const wrap: React.CSSProperties = { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: '-apple-system, system-ui, sans-serif' };
  const inner: React.CSSProperties = { maxWidth: 880, margin: '0 auto', padding: '32px 20px' };
  const card: React.CSSProperties = { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 };

  if (loading) return <div style={wrap}><div style={inner}><p style={{ color: C.sub }}>Loading…</p></div></div>;
  if (error || !d) return <div style={wrap}><div style={inner}><button onClick={() => navigate('/admin')} style={backBtn}>← Back</button><p style={{ color: '#F87171', marginTop: 16 }}>{error || 'Not found'}</p></div></div>;

  const pw = PAYWALL_LABEL[d.paywall] || PAYWALL_LABEL.not_reached;

  return (
    <div style={wrap}>
      <div style={inner}>
        <button onClick={() => navigate('/admin')} style={backBtn}>← Users</button>

        {/* header */}
        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>{d.name || 'Unnamed'}</h1>
            <div style={{ color: C.sub, marginTop: 4, fontSize: 14 }}>{d.is_guest ? 'Guest device' : d.email}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {d.is_tester && (
              <span style={{ ...chip, color: '#F5A623', background: '#3a2a12' }}>🧪 Tester</span>
            )}
            <span style={{ ...chip, color: d.signed_up ? '#4ADE80' : C.sub, background: d.signed_up ? '#123524' : C.panelAlt }}>
              {d.signed_up ? `Signed up · ${d.provider}` : 'Not signed up (guest)'}
            </span>
            {d.country && <span style={{ ...chip }}>{flagEmoji(d.country)} {d.city || d.country}</span>}
            <button
              onClick={toggleTester}
              disabled={toggling}
              style={{
                ...chip, cursor: toggling ? 'default' : 'pointer', border: `1px solid ${C.border}`,
                background: 'transparent', color: d.is_tester ? '#4ADE80' : '#F5A623', opacity: toggling ? 0.6 : 1,
              }}
            >
              {d.is_tester ? 'Unmark tester' : 'Mark as tester'}
            </button>
          </div>
        </div>

        {/* status cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 14, marginTop: 22 }}>
          <div style={card}>
            <div style={kLabel}>Plan</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{d.plan}{d.period ? ` · ${d.period}` : ''}</div>
          </div>
          <div style={card}>
            <div style={kLabel}>Paywall</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, color: pw.color }}>{pw.text}</div>
          </div>
          <div style={card}>
            <div style={kLabel}>Onboarding</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, color: d.onboarding.completed ? '#4ADE80' : '#F5A623' }}>
              {d.onboarding.completed ? 'Completed' : d.onboarding.steps.length ? `Dropped at ${d.onboarding.last_step}` : 'No data'}
            </div>
          </div>
          <div style={card}>
            <div style={kLabel}>Joined</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6 }}>{fmt(d.created_at)}</div>
          </div>
        </div>

        {/* subscription & billing */}
        {d.subscription?.started_at && (() => {
          const s = d.subscription!;
          const dates: { label: string; value: string | null; color?: string }[] = [
            { label: s.trial_ends_at ? 'Trial started' : 'Subscription started', value: s.started_at },
            { label: 'Trial ends', value: s.trial_ends_at },
            { label: 'Current period ends / next bill', value: s.current_period_end },
            { label: 'First payment', value: s.first_payment_at },
            { label: 'Last payment', value: s.last_payment_at },
            { label: 'Cancelled', value: s.cancelled_at, color: '#F87171' },
          ].filter((x) => x.value);
          return (
            <>
              <h2 style={h2}>Subscription &amp; Billing</h2>
              <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: '14px 24px' }}>
                {dates.map((x) => (
                  <div key={x.label}>
                    <div style={kLabel}>{x.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4, color: x.color || C.text }}>{fmt(x.value!)}</div>
                  </div>
                ))}
              </div>

              {d.payments && d.payments.length > 0 && (
                <div style={{ ...card, marginTop: 14 }}>
                  <div style={{ ...kLabel, marginBottom: 10 }}>Payment &amp; event history</div>
                  {d.payments.map((p, i) => {
                    const b = EVENT_BADGE[p.event_type] || { bg: C.panelAlt, fg: C.sub, label: p.event_type };
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: i < d.payments!.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                        <span style={{ background: b.bg, color: b.fg, padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{b.label}</span>
                        <div style={{ flex: 1, fontSize: 13, color: C.sub }}>{fmt(p.event_at)}</div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>
                          {p.amount_usd > 0 ? `$${p.amount_usd.toFixed(2)}` : '—'}
                          {p.amount_usd > 0 && p.currency && p.currency !== 'USD' && <span style={{ color: C.sub, fontSize: 12, marginLeft: 5 }}>({p.currency})</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}

        {/* acquisition (Apple Search Ads) */}
        {d.acquisition && d.acquisition.attributed !== null && (() => {
          const a = d.acquisition!;
          const fields: { label: string; value: string | null }[] = a.attributed ? [
            { label: 'Source', value: `Apple Search Ads · ${campaignName(String(a.campaign_id))}` },
            { label: 'Ad group', value: a.ad_group_id ? `#${a.ad_group_id}` : null },
            { label: 'Keyword', value: a.keyword_id ? `#${a.keyword_id}` : null },
            { label: 'Click type', value: a.conversion_type },
            { label: 'Click country', value: a.country },
            { label: 'Clicked ad', value: a.click_date ? fmt(a.click_date) : null },
          ].filter((x) => x.value) : [{ label: 'Source', value: 'Organic (not from an ad)' }];
          return (
            <>
              <h2 style={h2}>Acquisition</h2>
              <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: '14px 24px' }}>
                {fields.map((x) => (
                  <div key={x.label}>
                    <div style={kLabel}>{x.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4, color: x.label === 'Source' && a.attributed ? C.accent : C.text }}>{x.value}</div>
                  </div>
                ))}
              </div>
            </>
          );
        })()}

        {/* onboarding journey — latest run only; reinstalls/test devices each
            add a run under the same user_id, which used to replay every screen. */}
        <h2 style={h2}>
          Onboarding Journey
          {(d.onboarding.runs ?? 0) > 1 && (
            <span style={{ fontSize: 13, fontWeight: 500, color: C.sub, marginLeft: 10 }}>
              latest of {d.onboarding.runs} runs
            </span>
          )}
        </h2>
        {d.onboarding.steps.length === 0 ? (
          <p style={{ color: C.sub, fontSize: 14 }}>No step data (joined before tracking shipped).</p>
        ) : (
          <div style={{ ...card, borderLeft: `1px solid ${C.border}` }}>
            <div style={{ borderLeft: `2px solid ${C.border}`, paddingLeft: 18, marginLeft: 4 }}>
              {d.onboarding.steps.map((s, i) => {
                const last = i === d.onboarding.steps.length - 1;
                return (
                  <div key={i} style={{ position: 'relative', paddingBottom: 14 }}>
                    <span style={{ position: 'absolute', left: -25, top: 3, width: 10, height: 10, borderRadius: 5, background: last && !d.onboarding.completed ? '#F5A623' : C.accent }} />
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{s.step_key}</div>
                    <div style={{ fontSize: 12, color: C.sub }}>{fmt(s.viewed_at)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* answers */}
        {Object.keys(d.onboarding.answers).length > 0 && (
          <>
            <h2 style={h2}>Onboarding Answers</h2>
            <div style={card}>
              {Object.entries(d.onboarding.answers).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ color: C.sub }}>{k}</span>
                  <span style={{ fontWeight: 600, textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* recent activity */}
        <h2 style={h2}>Recent Activity</h2>
        {d.activity.length === 0 ? (
          <p style={{ color: C.sub, fontSize: 14 }}>No activity yet — no scans, diagnoses, or posts.</p>
        ) : (
          <div style={card}>
            {d.activity.map((a, i) => {
              const label = `${ACT_LABEL[a.type] || a.type}${a.title && a.title !== 'Scan' ? ` · ${a.title}` : ''}`;
              const open = () => a.image_url && setPhoto({ url: a.image_url, caption: label });
              return (
                <div
                  key={i}
                  onClick={open}
                  title={a.image_url ? 'Click to load and view the photo' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                    borderBottom: i < d.activity.length - 1 ? `1px solid ${C.border}` : 'none',
                    cursor: a.image_url ? 'pointer' : 'default',
                  }}
                >
                  {/* No thumbnails: 25 rows would mean 25 full-size image
                      fetches from R2 on every page view. The photo is only
                      requested once the name is clicked. */}
                  <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{ACT_ICON[a.type] || '•'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {a.image_url ? (
                        <span style={{ color: '#4ADE80', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                          {label}
                        </span>
                      ) : label}
                    </div>
                    <div style={{ fontSize: 12, color: C.sub }}>{fmt(a.created_at)}</div>
                  </div>
                  {a.image_url && <span style={{ color: C.sub, fontSize: 12 }}>📷</span>}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ height: 40 }} />
      </div>

      {/* Full-size photo. Click anywhere or press the close button to dismiss. */}
      {photo && (
        <div
          onClick={() => setPhoto(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', textAlign: 'center' }}>
            <img
              src={photo.url}
              alt={photo.caption}
              style={{ maxWidth: '90vw', maxHeight: '78vh', borderRadius: 12, display: 'block', margin: '0 auto' }}
            />
            <div style={{ marginTop: 12, color: '#f2f2f3', fontSize: 14, fontWeight: 600 }}>{photo.caption}</div>
            <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center' }}>
              <a
                href={photo.url}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#8a8a90', fontSize: 13, textDecoration: 'none' }}
              >
                Open original ↗
              </a>
              <button
                onClick={() => setPhoto(null)}
                style={{ background: 'transparent', border: 'none', color: '#8a8a90', fontSize: 13, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const backBtn: React.CSSProperties = { background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', color: C.text, cursor: 'pointer', fontSize: 14 };
const chip: React.CSSProperties = { background: C.panelAlt, color: C.text, padding: '5px 12px', borderRadius: 100, fontSize: 13 };
const kLabel: React.CSSProperties = { fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.5 };
const h2: React.CSSProperties = { fontSize: 18, fontWeight: 800, marginTop: 30, marginBottom: 12 };

export default UserDetail;
